import { LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssistantFeedback } from "./AssistantFeedback";
import { AssistantMarkdown } from "./assistantMarkdown";
import { AssistantStatsLine } from "./AssistantStatsLine";
import { ConfidenceBadge } from "./ConfidenceBadge";
import type { AssistantTurn } from "./useAssistantChat";

/** Секундите от изпращането — отговорът идва след 6–20 s, понякога повече. */
const ElapsedSeconds = ({ startedAt }: { startedAt: number }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.round((now - startedAt) / 1000));
  return (
    <div className="flex items-center gap-[8px] text-[13px] text-muted" role="status">
      <LoadingOutlined className="!text-[14px] !text-brand-ink" />
      <span className="tabular-nums">{t("monitoring.assistantThinking", { seconds })}</span>
    </div>
  );
};

export const AssistantTurnView = ({ turn, isNarrow }: { turn: AssistantTurn; isNarrow: boolean }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-[10px]">
      {/* Въпросът — вдясно, като реплика; whitespace-pre-wrap пази новите редове (Shift+Enter) */}
      <div
        className={`self-end whitespace-pre-wrap break-words rounded-[14px] rounded-br-[4px] bg-brand-tint px-[14px] py-[9px] text-[14px] leading-[1.5] text-ink ${
          isNarrow ? "max-w-[92%]" : "max-w-[75%]"
        }`}
      >
        {turn.question}
      </div>

      <div
        className={`flex flex-col gap-[12px] rounded-[14px] border border-shade-2 bg-surface ${
          isNarrow ? "px-[14px] py-[14px]" : "px-[20px] py-[16px]"
        }`}
      >
        {turn.status === "pending" && <ElapsedSeconds startedAt={turn.startedAt} />}

        {turn.status === "error" && (
          <div className="flex items-start gap-[8px] text-[13.5px] leading-[1.5] text-danger-ink">
            <WarningOutlined className="mt-[3px] !text-[14px]" />
            <span>{t(turn.errorKey ?? "monitoring.assistantErrGeneric")}</span>
          </div>
        )}

        {turn.status === "done" && turn.answer && (
          <>
            {turn.answer.confidence && <ConfidenceBadge confidence={turn.answer.confidence} />}
            <AssistantMarkdown text={turn.answer.answer} />
            <div className="flex flex-col gap-[10px] border-t border-shade-2 pt-[10px]">
              <AssistantStatsLine stats={turn.answer.stats} />
              <AssistantFeedback exchangeId={turn.answer.id} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
